import { Injectable, computed, signal, type Signal } from '@angular/core';
import { Subject } from 'rxjs';

export type SelectionMode = 'explicit' | 'allMatching';

export interface CollectionSelectionState {
  selectedIds: ReadonlySet<string>;
  excludedIds: ReadonlySet<string>;
  mode: SelectionMode;
  total: number;
  filterSnapshot: unknown;
}

@Injectable()
export class CollectionSelectionService {
  private readonly _selectedIds = signal<ReadonlySet<string>>(new Set());
  private readonly _excludedIds = signal<ReadonlySet<string>>(new Set());
  private readonly _mode = signal<SelectionMode>('explicit');
  private readonly _total = signal(0);
  private readonly _filterSnapshot = signal<unknown>(undefined);

  readonly cleared$ = new Subject<void>();

  readonly selectedIds: Signal<ReadonlySet<string>> = this._selectedIds.asReadonly();
  readonly excludedIds: Signal<ReadonlySet<string>> = this._excludedIds.asReadonly();
  readonly mode: Signal<SelectionMode> = this._mode.asReadonly();
  readonly total: Signal<number> = this._total.asReadonly();
  readonly filterSnapshot: Signal<unknown> = this._filterSnapshot.asReadonly();

  readonly count = computed(() => {
    if (this._mode() === 'allMatching') return Math.max(0, this._total() - this._excludedIds().size);
    return this._selectedIds().size;
  });

  readonly state = computed<CollectionSelectionState>(() => ({
    selectedIds: this._selectedIds(),
    excludedIds: this._excludedIds(),
    mode: this._mode(),
    total: this._total(),
    filterSnapshot: this._filterSnapshot(),
  }));

  toggle(id: string): void {
    if (this._mode() === 'allMatching') {
      this._excludedIds.update(previous => {
        const next = new Set(previous);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
      return;
    }

    this._selectedIds.update(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  selectAllMatching(total: number, filterSnapshot: unknown): void {
    this._total.set(Math.max(0, total));
    this._filterSnapshot.set(filterSnapshot);
    this._mode.set('allMatching');
    this._selectedIds.set(new Set());
    this._excludedIds.set(new Set());
  }

  snapshotFilter(currentFilter: unknown): void {
    if (this._mode() === 'allMatching') {
      const prev = this._filterSnapshot();
      if (!deepEqual(prev, currentFilter)) {
        this.clear();
        this.cleared$.next();
        return;
      }
    }
    this._filterSnapshot.set(currentFilter);
  }

  clear(): void {
    this._selectedIds.set(new Set());
    this._excludedIds.set(new Set());
    this._mode.set('explicit');
    this._total.set(0);
    this._filterSnapshot.set(undefined);
  }

  isSelected(id: string): boolean {
    if (this._mode() === 'allMatching') return !this._excludedIds().has(id);
    return this._selectedIds().has(id);
  }
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || a === undefined || b === undefined) return a === b;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((value, index) => deepEqual(value, b[index]));
  }

  if (!isRecord(a) || !isRecord(b)) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;

  return keysA.every(key => key in b && deepEqual(a[key], b[key]));
}

function isRecord(value: object): value is Record<string, unknown> {
  return !Array.isArray(value);
}
